import React from "react";
import { auth } from "../firebase.config";
import { readPageCache } from "../utility/pageCache";
import { getDiscount, getTotal } from "../Data/Functions";
import { invoicePill, orderPill, quotePill } from "../utility/clientStatus";
import { languagePair, money, shortDate } from "../utility/clientLists";
import "./ClientUi.css";
import "./LastVisit.css";

// The last version of Orders, Quotes or Invoices a person saw (utility/pageCache,
// kept by Hooks/useLastVisit), drawn in the portal skeleton at once, grey and
// not clickable, while the profile and the search load: they used to see a
// spinner for the first second or two. The real page takes over in its own
// layout and keeps showing this copy until it has this visit's data.

const KINDS = {
  "/account/orders": { kind: "orders", title: "Orders", number: "orderNumber", head: "Order" },
  "/account/quotes": { kind: "quotes", title: "Quotes", number: "quoteNumber", head: "Quote" },
  "/account/invoices": { kind: "invoices", title: "Invoices", number: "invoiceNumber", head: "Invoice" },
};

// What the last visit kept for this address, or null. The team comes from the
// ID this browser remembers for the person (App.js), the same scope the real
// page reads the copy with.
export const cachedPreviewFor = (pathname) => {
  const spec = KINDS[String(pathname || "").replace(/\/+$/, "")];
  const user = auth.currentUser;
  if (!spec || !user) return null;
  let team = "";
  try {
    team = localStorage.getItem(`everbary.teamOf:${user.uid}`) || "";
  } catch (e) {
    team = "";
  }
  const data = readPageCache(spec.kind, user.uid, null, team ? `team:${team}` : "personal");
  if (!data || !Array.isArray(data.rows) || data.rows.length === 0) return null;
  return { spec, rows: data.rows, total: data.total || data.rows.length };
};

const totalOf = (kind, row) => {
  try {
    if (kind === "invoices") return money(row.totalPrice);
    if (kind === "quotes") {
      const stored = Number(row.totalPrice);
      if (!["replied", "ordered", "cancelled"].includes(row.status)) return "—";
      if (Number.isFinite(stored) && stored > 0) return money(stored);
      return row.pagesCount ? money(getTotal(row) - getDiscount(row)) : "—";
    }
    return row.pagesCount === undefined && row.wordCount === undefined ? "" : money(getTotal(row) - getDiscount(row));
  } catch (e) {
    return "";
  }
};

const pillOf = (kind, row) => (kind === "invoices" ? invoicePill(row) : kind === "quotes" ? quotePill(row) : orderPill(row));

const CachedPreview = ({ preview }) => {
  const { spec, rows, total } = preview;
  const clientOf = (r) => [r.clientFirstName, r.clientLastName].filter(Boolean).join(" ");
  return (
    <div className="cl clPage" data-testid="cached-preview">
      <div className="clHead"><h1 className="clTitle">{spec.title}</h1></div>
      <div className="clListBody" data-stale="true" aria-busy="true" inert="">
        <div className="clTableWrap">
          <table className="clTable clCards">
            <thead>
              <tr>
                <th>{spec.head}</th>
                <th>Client · case</th>
                {spec.kind !== "invoices" && <th>Language</th>}
                <th>Status</th>
                <th className="clNum">{spec.kind === "invoices" ? "Amount" : "Total"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const pill = pillOf(spec.kind, row);
                const lang = languagePair(row);
                return (
                  <tr key={row.objectID || row.id || i} className="clRow">
                    <td className="sMain">
                      <div className="clStrong">#{row[spec.number]}</div>
                      <div className="clMut">{shortDate(row.timestamp)}</div>
                    </td>
                    <td className="sMeta">
                      <div className="clEllipsis clName">{clientOf(row) || (row.caseNumber ? "" : "—")}</div>
                      {row.caseNumber && <div className="clMut"><span className="clientCaseNumber">{row.caseNumber}</span></div>}
                    </td>
                    {spec.kind !== "invoices" && <td className="sHide"><span className="clLang">{lang.short}</span></td>}
                    <td className="sAside"><span className="clPill" style={{ background: pill.color }}>{pill.label}</span></td>
                    <td className="clNum clStrong sAside2">{totalOf(spec.kind, row)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="clMut" style={{ padding: "10px 4px", fontSize: 12.5 }}>{`1–${rows.length} of ${total}`}</div>
      </div>
    </div>
  );
};

export default CachedPreview;
